import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Accordion,
    AccordionItem,
    Button,
    Input,
    Label,
    Modal,
    ModalHeader,
} from "../../Components/Common/Tailwind";
import moment from "moment";
import { getToday } from "../../common/utils/dateUtil";

import { API } from "../../api";
import { useUserStore } from "../../store/store";
import QuantityButtonInput from "../../Components/Common/QuantityButtonInput";
import CommonDatePicker from "../../Components/Common/CommonDatePicker";
import { DetailImgModal, DetailInfoModal, DetailUseAdviceModal } from "../../Components/modal/ManagerModal";
import LoginPromptModal from "../../Components/modal/LoginPromptModal";
import { Link } from "react-router-dom";
import AmCarousel from "../../Components/AmCarousel";
import { MapPin, Info, CalendarIcon } from "lucide-react";
import { numberWithCommas } from "../../common/utils/format_util";
import { seatLayoutService } from "../../api/services/seat-layout.service";
import SeatLayoutZoomViewer from "./SeatLayoutZoomViewer";
import {
    buildFreeSeatSelection,
    clampFreeSeatQuantity,
    getFreeSeatRemainingCount,
    getSeatDisplayName,
    getSeatOptionKey,
    isReservationOptionForSeat,
    isReservationOptionSelected,
} from "./freeSeatSelection.mjs";
import { getOptionPersonnelValidation, shouldEnterOptionStep } from "./reservationProcess.mjs";
import { parsePackageTagline } from "./packageTagline.mjs";

const KOREAN_WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];
const formatKoreanDateWithWeekday = (date) => {
    if (!date) return "-";
    const targetDate = moment(date);
    return `${targetDate.format("YYYY년 M월 D일")} (${KOREAN_WEEKDAYS[targetDate.day()]})`;
};

const formatCompactDateWithWeekday = (date) => {
    if (!date) return "-";
    const targetDate = moment(date);
    return `${targetDate.format("YYYY.MM.DD")} (${KOREAN_WEEKDAYS[targetDate.day()]})`;
};

const getLaterDateValue = (firstDate, secondDate) => {
    if (!firstDate) return secondDate;
    if (!secondDate) return firstDate;
    return firstDate > secondDate ? firstDate : secondDate;
};

const getPackageImageSrc = (imageItem) => {
    if (!imageItem) return "";
    if (typeof imageItem === "string") return imageItem;
    return imageItem.src || imageItem.image || imageItem.url || imageItem.image_url || imageItem.thumbnail || imageItem.thumbnail_url || "";
};

const normalizeCarouselImageItem = (imageItem, index) => {
    const src = getPackageImageSrc(imageItem);
    if (!src) return null;
    return {
        ...(typeof imageItem === "object" ? imageItem : {}),
        src,
        key: imageItem?.key ?? imageItem?.id ?? index,
    };
};

function PackageDetailSection({
    id = null,
    privateOfferToken = "",
    privateOfferItemId = "",
    privateOfferReservationStartDate = "",
    privateOfferReservationEndDate = "",
    initialProduct = null,
    initialSelectedSpotId = [],
    onProgressChange = () => {},
    onHeaderTitleChange = () => {},
    // startDate, // endDate is not used in standard package detail usually, or derived
    // selectedDate, selectedSpotId
}) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const isOptionStep = searchParams.get("step") === "options";
    const minimumSelectableDate = privateOfferToken
        ? getLaterDateValue(privateOfferReservationStartDate, moment(getToday()).format("YYYY-MM-DD"))
        : "today";

    // Date State Management
    const [startDate, setStartDate] = useState(searchParams.get("date") || minimumSelectableDate || moment(getToday()).format("YYYY-MM-DD"));


    const [product, setProduct] = useState(initialProduct || {});

    const [detailImageEditModal, setDetailImageEditModal] = useState(false);
    const [detailInfoModal, setDetailInfoModal] = useState(false);
    const [detailUseAdviceModal, setDetailUseAdviceModal] = useState(false);

    const [optionProducts, setOptionProducts] = useState([]);
    const [reservationOptions, setReservationOptions] = useState([]);

    const [packageSeats, setPackageSeats] = useState(initialProduct?.spots || []);
    const [selectedSeats, setSelectedSeats] = useState(initialSelectedSpotId || []);
    const [seatLayout, setSeatLayout] = useState(null);
    const [seatLayoutLoading, setSeatLayoutLoading] = useState(false);
    const [seatLayoutStatus, setSeatLayoutStatus] = useState("idle");
    const [seatLayoutMessage, setSeatLayoutMessage] = useState("");
    const [seatLayoutTooltipOpen, setSeatLayoutTooltipOpen] = useState(false);
    const [seatLayoutModalOpen, setSeatLayoutModalOpen] = useState(false);
    const [seatImageModalOpen, setSeatImageModalOpen] = useState(false);
    const [bookingLoading, setBookingLoading] = useState(false);

    const [seatSelectionWarning, setSeatSelectionWarning] = useState("");
    const [showLoginModal, setShowLoginModal] = useState(false);

    const [user] = useUserStore((state) => [state.user]);

    const abortRef = React.useRef(null);

    const setReservationStep = React.useCallback((step) => {
        const nextSearchParams = new URLSearchParams(searchParams);
        if (step) {
            nextSearchParams.set("step", step);
        } else {
            nextSearchParams.delete("step");
        }

        setSearchParams(nextSearchParams, { replace: false });
    }, [searchParams, setSearchParams]);

    const handleStartDateChange = React.useCallback((nextDate) => {
        if (!nextDate) return;
        setStartDate(nextDate);
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set("date", nextDate);
        nextSearchParams.delete("step");
        setSearchParams(nextSearchParams, { replace: true });
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        if (!privateOfferToken || !startDate) return;

        let nextDate = startDate;
        if (minimumSelectableDate && nextDate < minimumSelectableDate) {
            nextDate = minimumSelectableDate;
        }
        if (privateOfferReservationEndDate && nextDate > privateOfferReservationEndDate) {
            nextDate = privateOfferReservationEndDate;
        }
        if (nextDate !== startDate) {
            handleStartDateChange(nextDate);
        }
    }, [handleStartDateChange, minimumSelectableDate, privateOfferReservationEndDate, privateOfferToken, startDate]);

    const mapLayoutSeats = (layoutData) => layoutData.seats.map((seat) => ({
        id: seat.spot_id,
        name: seat.label || seat.spot_name,
        spot_name: seat.spot_name,
        is_free: false,
        is_able: Boolean(seat.availability?.can_select),
        reason: seat.availability?.reason,
        status: seat.availability?.status,
    }));

    const seatLayoutNotice = seatLayoutMessage && ["fallback", "error"].includes(seatLayoutStatus)
        ? {
            message: seatLayoutMessage,
            triggerClassName: seatLayoutStatus === "error"
                ? "text-[#e16036]"
                : "text-[#e16036]",
            panelClassName: seatLayoutStatus === "error"
                ? "border-[#f4c8cf] bg-[#fff7f4] text-[#e16036]"
                : "border-[#f4c8cf] bg-[#fff7f4] text-[#e16036]",
        }
        : null;

    useEffect(() => {
        if (!seatLayoutNotice) {
            setSeatLayoutTooltipOpen(false);
        }
    }, [seatLayoutNotice]);

    const isFreeSeatPackage = product?.has_spots === false;
    const shouldShowSeatMapButton = Boolean(product["seat_img"]);
    const seatSectionTitle = isFreeSeatPackage ? "수량 선택" : "좌석 선택";
    const freeSeatRemainingCount = isFreeSeatPackage ? getFreeSeatRemainingCount(product) : 0;
    const freeSeatSpot = isFreeSeatPackage ? packageSeats?.[0] : null;
    const optionSeatItems = selectedSeats;
    const hasLayoutSeats = Boolean(seatLayout?.seats?.length);
    const hasAvailableLayoutSeat = hasLayoutSeats
        ? seatLayout.seats.some((seat) => Boolean(seat.availability?.can_select))
        : false;
    const hasFallbackSeats = Boolean(packageSeats?.length);
    const hasAvailableFallbackSeat = hasFallbackSeats
        ? packageSeats.some((spot) => Boolean(spot.is_able) && (product.price !== 0 || product.allow_free_booking))
        : false;
    const isSeatSelectionUnavailable = !isFreeSeatPackage && product?.has_spots && !seatLayoutLoading && (
        hasLayoutSeats ? !hasAvailableLayoutSeat : hasFallbackSeats && !hasAvailableFallbackSeat
    );
    const productImageItems = (product.images || [])
        .map((item, index) => normalizeCarouselImageItem(item, index))
        .filter(Boolean);
    const taglineBadges = parsePackageTagline(product?.tagline);

    useEffect(() => {
        if (!startDate || (!id && !privateOfferToken)) return;

        const applySeatLayout = (layoutData) => {
            setSeatLayout(layoutData);
            if (!layoutData) {
                return;
            }
            setPackageSeats(mapLayoutSeats(layoutData));
        };

        const fetchData = async () => {
            try {
                setSelectedSeats([]);
                setReservationOptions([]);
                setSeatSelectionWarning("");
                setSeatLayoutModalOpen(false);
                setSeatImageModalOpen(false);

                if (abortRef.current) {
                    abortRef.current.abort();
                }
                const abortController = new AbortController();
                const signal = abortController.signal;
                abortRef.current = abortController;

                let productData;
                let productDateData;
                if (privateOfferToken) {
                    const privateOfferResponse = await API.get(`/package/private-offers/${privateOfferToken}/?item_id=${privateOfferItemId}&date=${startDate}`, {
                        signal: signal,
                    });
                    productData = {
                        ...privateOfferResponse.data.package,
                        price: privateOfferResponse.data.price,
                        private_offer_token: privateOfferToken,
                        private_offer_item_id: privateOfferResponse.data.item_id || privateOfferItemId,
                    };
                    productDateData = privateOfferResponse.data;
                } else {
                    const productResponse = await API.get(`/package/items/${id}/`);
                    productData = productResponse.data;

                    const productDateRes = await API.get(`/package/items/?date=${startDate}&id=${id}`, {
                        signal: signal,
                    });
                    productDateData = productDateRes.data[0];
                }

                setOptionProducts(productData.options);

                let layoutData = null;
                if (productData.has_spots) {
                    setSeatLayoutLoading(true);
                    setSeatLayoutStatus("loading");
                    setSeatLayoutMessage("");
                    try {
                        const layoutResponse = await seatLayoutService.getPublishedByPackage(productData.id, { date: startDate });
                        layoutData = layoutResponse.data;
                        setSeatLayoutStatus("ready");
                    } catch (layoutError) {
                        if (layoutError?.response?.status === 404) {
                            setSeatLayoutStatus("fallback");
                            setSeatLayoutMessage("기본 좌석 목록입니다.");
                        } else {
                            setSeatLayoutStatus("error");
                            setSeatLayoutMessage("좌석 정보를 불러올 수 없습니다. 잠시 후 다시 시도해 주세요.");
                            console.log(layoutError);
                        }
                    } finally {
                        setSeatLayoutLoading(false);
                    }
                } else {
                    setSeatLayoutStatus("idle");
                    setSeatLayoutMessage("");
                }

                if (layoutData) {
                    applySeatLayout(layoutData);
                } else {
                    setSeatLayout(null);
                    setPackageSeats(productDateData.spots);
                }

                if (!privateOfferToken && user && user.membership && productDateData.membership_price && productDateData.membership_price < productDateData.price) {
                    setProduct({
                        ...productData,
                        price: productDateData.membership_price,
                        membershipApplied: true,
                        reserved: productDateData.reserved,
                        count: productDateData.count,
                    });
                } else {
                    setProduct({
                        ...productData,
                        price: productDateData.price,
                        membershipApplied: false,
                        reserved: productDateData.reserved,
                        count: productDateData.count,
                    });
                }

                const isSpotFree = !productData.has_spots
                if (isSpotFree && productDateData.spots?.[0]) {
                    const remainingCount = getFreeSeatRemainingCount(productDateData);
                    const quantity = clampFreeSeatQuantity(1, remainingCount);
                    const freeSeatSelection = buildFreeSeatSelection(productDateData.spots[0], quantity);
                    setSeatLayout(null);
                    setSelectedSeats(freeSeatSelection);
                }

            } catch (error) {
                console.log(error);
                setSeatLayoutLoading(false);
            }
        };

        fetchData();
    }, [id, privateOfferToken, privateOfferItemId, startDate, user]);


    const handleSeats = (spot) => {
        setSeatSelectionWarning("");

        if (!selectedSeats?.length) {
            setSelectedSeats([{ id: spot.id, name: spot.name }]);
        } else if (!selectedSeats.find(item => item.id === spot.id)) {
            setSelectedSeats([...selectedSeats, { id: spot.id, name: spot.name }]);
        } else {
            setSelectedSeats(selectedSeats.filter((ele) => ele.id !== spot.id));

            // remove options
            const _reservOptions = [...reservationOptions];
            const matchedOptions = _reservOptions.filter((value) => value.spotId === spot.id);

            for (let i = 0; i < matchedOptions.length; i++) {
                const _optionIdx = _reservOptions.findIndex((value) => {
                    return value.spotId === spot.id && value.optionId === matchedOptions[i].optionId;
                });
                if (_optionIdx !== -1) {
                    _reservOptions.splice(_optionIdx, 1);
                }
            }

            setReservationOptions(_reservOptions);
        }
    }

    const handleFreeSeatQuantity = (quantity) => {
        setSeatSelectionWarning("");
        const nextQuantity = clampFreeSeatQuantity(quantity, freeSeatRemainingCount);
        const nextSeats = buildFreeSeatSelection(freeSeatSpot, nextQuantity);
        const nextSeatKeys = new Set(nextSeats.map((seat) => seat.clientKey));
        setSelectedSeats(nextSeats);
        setReservationOptions((prev) => prev.filter((item) => !item.seatKey || nextSeatKeys.has(item.seatKey)));
    };

    const handleReservationOption = (checkValue, spotId, optionId, name, price, type, quantity, taxfree = false, seatKey = null) => {
        let _reservOptions = [...reservationOptions];
        const _optionIdx = _reservOptions.findIndex((value) => {
            return value.spotId === spotId && value.optionId === optionId && (value.seatKey || null) === (seatKey || null);
        });
        if (_optionIdx !== -1) {
            if (!checkValue) {
                // remove
                _reservOptions.splice(_optionIdx, 1);
            }
        } else if (checkValue) {
            // add
            _reservOptions = [
                ...reservationOptions,
                {
                    spotId: spotId,
                    name: name,
                    optionId: optionId,
                    seatKey: seatKey,
                    price: price,
                    type: type,
                    quantity: quantity,
                    taxfree: taxfree,
                },
            ];
        }

        setReservationOptions(_reservOptions);
    };

    const handleReservationOptionQuantity = reservationOptions.filter((item) => {
        return item.quantity > 0;
    }).flatMap((item) => {
        const clonedItems = [];
        for (let i = 0; i < item.quantity; i++) {
            clonedItems.push({ ...item });
        }
        return clonedItems;
    });

    const selectableOptionProducts = optionProducts?.filter((item) => item.type !== "deposit") || [];
    const hasSelectableOptions = selectableOptionProducts.length > 0;
    const requiredOptionProducts = product.option_required
        ? selectableOptionProducts.filter((item) => item.type === product.option_required_type)
        : [];
    const optionalOptionProducts = product.option_required
        ? selectableOptionProducts.filter((item) => item.type !== product.option_required_type)
        : selectableOptionProducts;

    useEffect(() => {
        if (isOptionStep && (!selectedSeats.length || !hasSelectableOptions)) {
            setReservationStep(null);
        }
    }, [hasSelectableOptions, isOptionStep, selectedSeats.length, setReservationStep]);

    const hasRequiredOptionSelected = !product.option_required ||
        optionSeatItems.every((seatItem) => {
            return handleReservationOptionQuantity.some((value) => {
                return value.type === product.option_required_type && isReservationOptionForSeat(value, seatItem);
            });
        });
    const shouldMoveToOptionStep = shouldEnterOptionStep({
        selectedSeatCount: selectedSeats.length,
        hasSelectableOptions,
        isOptionStep,
    });
    const bookingButtonLabel = bookingLoading
        ? "확인 중..."
        : isSeatSelectionUnavailable
            ? "예약 가능한 좌석이 없습니다"
            : selectedSeats.length === 0
                ? isFreeSeatPackage ? "수량을 선택해주세요" : "좌석을 선택해주세요"
                : shouldMoveToOptionStep
                    ? "옵션 선택"
                    : !hasRequiredOptionSelected
                        ? "필수 옵션 선택"
                        : isFreeSeatPackage
                            ? `자유석 ${selectedSeats.length}개 예약하기`
                            : selectedSeats.length === 1
                                ? `${getSeatDisplayName(selectedSeats[0]) || "좌석"} 예약하기`
                                : `좌석 ${selectedSeats.length}개 예약하기`;
    const totalAmount = product.price
        ? product.price * selectedSeats.length + reservationOptions.reduce((acc, curr) => acc + (curr.price * curr.quantity), 0)
        : 0;

    useEffect(() => {
        onProgressChange(isOptionStep ? 75 : 50);
        onHeaderTitleChange(isOptionStep ? "예약 옵션 선택" : "예약 상세");
    }, [isOptionStep, onHeaderTitleChange, onProgressChange]);

    const navigateToOrder = () => {
        navigate(`/order`, {
            state: {
                type: 0, // "package"
                product: product,
                selectDate: startDate,
                startDate,
                endDate: startDate,
                spotId: selectedSeats,
                options: handleReservationOptionQuantity,
                quantity: 1, // 나중에 티켓 수량 선택 시 이용할 필드
                membership_product_id: "",
                private_offer_token: privateOfferToken,
                private_offer_item_id: product.private_offer_item_id || privateOfferItemId,
            },
        });
    };

    const handleBookingSubmit = async () => {
        if (!user) {
            setShowLoginModal(true);
            return;
        }

        if (!selectedSeats.length) {
            toast.warning("자리를 선택해 주세요.");
            return;
        }

        if (shouldMoveToOptionStep) {
            setReservationStep("options");
            return;
        }

        if (product.option_required) {
            const optionPersonnelValidation = getOptionPersonnelValidation({
                product,
                seats: optionSeatItems,
                options: handleReservationOptionQuantity,
            });
            if (optionPersonnelValidation) {
                toast.warning(optionPersonnelValidation.message);
                if (!isOptionStep && hasSelectableOptions) {
                    setReservationStep("options");
                }
                return;
            }

            const missingRequiredOption = optionSeatItems.find((seatItem) => {
                return !handleReservationOptionQuantity.find((value) => {
                    return value.type === product.option_required_type && isReservationOptionForSeat(value, seatItem);
                });
            });
            if (missingRequiredOption) {
                toast.warning("필수 옵션을 선택해 주세요.");
                return;
            }
        }

        if (seatLayout?.id) {
            setBookingLoading(true);
            try {
                const checkResponse = await seatLayoutService.reserveCheck(seatLayout.id, {
                    date: startDate,
                    spot_ids: selectedSeats.map((item) => item.id),
                });
                if (!checkResponse.data.valid) {
                    const invalidSpotIds = checkResponse.data.invalid_spot_ids;
                    setSelectedSeats((prev) => prev.filter((item) => !invalidSpotIds.includes(item.id)));
                    setReservationOptions((prev) => prev.filter((item) => !invalidSpotIds.includes(item.spotId)));
                    setSeatSelectionWarning("선택한 좌석 중 예약 불가능한 좌석이 있어 자동 해제되었습니다. 다시 선택해 주세요.");
                    const refreshedLayout = await seatLayoutService.getAvailability(seatLayout.id, { date: startDate });
                    setSeatLayout(refreshedLayout.data);
                    setSeatLayoutStatus("ready");
                    setSeatLayoutMessage("");
                    setPackageSeats(mapLayoutSeats(refreshedLayout.data));
                    return;
                }
            } catch (error) {
                toast.error(error?.message || "좌석 상태를 확인할 수 없습니다. 다시 시도해 주세요.");
                return;
            } finally {
                setBookingLoading(false);
            }
        }

        navigateToOrder();
    };

    const renderOptionControls = () => (
        <>
            {optionSeatItems.length > 0 ? (
                optionSeatItems.map((spotItem, seatIndex) => {
                    const selectedOptionsForSeat = reservationOptions
                        .map((options, index) => ({ options, index }))
                        .filter(({ options }) => options.quantity > 0 && isReservationOptionForSeat(options, spotItem));

                    return (
                        <div
                            key={spotItem.clientKey || spotItem.id}
                            className={`mb-6 last:mb-0 ${seatIndex > 0 ? "border-t border-gray-300 pt-6" : ""}`}
                        >
                            <div className="flex items-center gap-2 mb-3">
                                <div className="bg-black text-white px-2 py-1 rounded text-xs font-bold">자리 {getSeatDisplayName(spotItem)}</div>
                                <span className="text-sm text-gray-700">옵션을 선택하세요</span>
                            </div>

                            {selectableOptionProducts.length ? (
                                <Accordion>
                                    {requiredOptionProducts.length ? (
                                        <AccordionItem title={<span style={{ fontFamily: "Gmarket-M" }}>필수 옵션 선택 </span>}>
                                            <div className="">
                                                {requiredOptionProducts.map((optionItem) => (
                                                    <div
                                                        key={optionItem.id}
                                                        className="my-2 mx-2 grid min-h-9 grid-cols-[16px_minmax(0,1fr)_auto] items-center gap-x-2 border-b border-gray-200 py-2"
                                                        dir="ltr"
                                                    >
                                                        <Input
                                                            type="checkbox"
                                                            className="m-0 h-4 w-4 shrink-0 self-center accent-[#e16036]"
                                                            id={getSeatOptionKey(spotItem, optionItem.id)}
                                                            checked={reservationOptions.some((value) => isReservationOptionSelected(value, spotItem, optionItem.id))}
                                                            disabled={optionItem.type === "soldout"}
                                                            onChange={(e) => {
                                                                handleReservationOption(e.currentTarget.checked, spotItem.id, optionItem.id, optionItem.name, optionItem.price, optionItem.type, 1, optionItem.tax_free, spotItem.clientKey);
                                                            }}
                                                        />
                                                        <Label for={getSeatOptionKey(spotItem, optionItem.id)} className="mb-0 flex h-full min-w-0 items-center px-3 text-sm leading-none text-gray-700" style={{ fontFamily: "Gmarket-M", marginBottom: 0 }}>
                                                            {optionItem.name} {optionItem.type === "soldout" ? <span className="text-danger font-bold">품절</span> : null}
                                                        </Label>
                                                        <span className="flex h-full shrink-0 items-center text-sm font-semibold leading-none text-gray-900">
                                                            {optionItem.price ? `+${numberWithCommas(optionItem.price)}원` : "0원"}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </AccordionItem>
                                    ) : null}

                                    {optionalOptionProducts.length ? (
                                        <AccordionItem title={<span style={{ fontFamily: "Gmarket-M" }}>선택 옵션</span>}>
                                            <div className="">
                                                {optionalOptionProducts.map((optionItem) => (
                                                    <div
                                                        key={optionItem.id}
                                                        className="my-2 mx-2 grid min-h-9 grid-cols-[16px_minmax(0,1fr)_auto] items-center gap-x-2 border-b border-gray-200 py-2"
                                                        dir="ltr"
                                                    >
                                                        <Input
                                                            type="checkbox"
                                                            className="m-0 h-4 w-4 shrink-0 self-center accent-[#e16036]"
                                                            id={getSeatOptionKey(spotItem, optionItem.id)}
                                                            checked={reservationOptions.some((value) => isReservationOptionSelected(value, spotItem, optionItem.id))}
                                                            disabled={optionItem.type === "soldout"}
                                                            onChange={(e) => {
                                                                handleReservationOption(e.currentTarget.checked, spotItem.id, optionItem.id, optionItem.name, optionItem.price, optionItem.type, 1, optionItem.tax_free, spotItem.clientKey);
                                                            }}
                                                        />
                                                        <Label for={getSeatOptionKey(spotItem, optionItem.id)} className="mb-0 flex h-full min-w-0 items-center px-3 text-sm leading-none text-gray-700" style={{ fontFamily: "Gmarket-M", marginBottom: 0 }}>
                                                            {optionItem.name} {optionItem.type === "soldout" ? <span className="text-danger font-bold">품절</span> : null}
                                                        </Label>
                                                        <span className="flex h-full shrink-0 items-center text-sm font-semibold leading-none text-gray-900">
                                                            {optionItem.price ? `+${numberWithCommas(optionItem.price)}원` : "0원"}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </AccordionItem>
                                    ) : null}
                                </Accordion>
                            ) : null}

                            {selectedOptionsForSeat.length > 0 ? (
                                <div className="mt-3 rounded-xl border border-gray-100 bg-gray-50 px-3 py-3">
                                    <h5 className="mb-2 text-xs font-bold text-gray-700">선택 내역</h5>
                                    {selectedOptionsForSeat.map(({ options, index }) => (
                                        <div
                                            key={"reservationOptions" + index}
                                            className="mb-2 flex items-center justify-between gap-3 rounded-lg border border-gray-100 bg-white px-3 py-2 last:mb-0"
                                        >
                                            <span className="min-w-0 text-xs font-semibold text-gray-700">
                                                {options.name}
                                            </span>

                                            <QuantityButtonInput
                                                quantity={reservationOptions[index].quantity}
                                                detailPage={true}
                                                variant="subtle"
                                                onChange={(value) => {
                                                    const _reservationOptions = [...reservationOptions];
                                                    if (_reservationOptions[index].type === "extra") {
                                                        if (value > product.max_personnel) {
                                                            toast.error(`최대 ${product.max_personnel}개까지 선택 가능합니다.`);
                                                            return;
                                                        }
                                                    }
                                                    _reservationOptions[index].quantity = value;
                                                    setReservationOptions(_reservationOptions);
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    );
                })
            ) : (
                <div className="flex min-h-[120px] flex-col items-center justify-center rounded-xl bg-gray-50 px-4 py-5 text-center">
                    <p className="mb-1 text-sm font-bold text-gray-700" style={{ fontFamily: "Gmarket-M" }}>
                        좌석을 먼저 선택해주세요.
                    </p>
                    <p className="mb-0 text-xs leading-5 text-gray-500">
                        좌석을 선택하면 해당 좌석의 옵션을 고를 수 있어요.
                    </p>
                </div>
            )}
        </>
    );

    const optionSelectionContent = (
        <div className="-mx-4 min-h-[calc(100vh-180px)] bg-gray-100 px-4 pb-28 pt-4">
            <div className="mb-4 rounded-3xl border border-gray-100 bg-white px-4 py-5 shadow-[0_2px_20px_rgba(0,0,0,0.04)]">
                <h3 className="mb-2 text-xl font-bold text-gray-900" style={{ fontFamily: "Gmarket-B" }}>옵션 선택</h3>
                <p className="mb-4 text-sm leading-6 text-gray-600">
                    선택한 좌석별로 필요한 옵션을 선택해 주세요.
                </p>
                <div className="flex flex-wrap gap-2">
                    {selectedSeats.map((seatItem, index) => (
                        <span
                            key={seatItem.clientKey || `${seatItem.id}-${index}`}
                            className="inline-flex items-center rounded-lg border border-[#f0d8c3] bg-black px-3 py-1 text-xs font-semibold text-white"
                        >
                            {getSeatDisplayName(seatItem)}
                        </span>
                    ))}
                </div>
            </div>

            <div className="rounded-3xl border border-gray-100 bg-white px-4 py-5 shadow-sm">
                {renderOptionControls()}
            </div>
        </div>
    );

    if (!startDate || !id) {
        return (
            <div>
                <div>Loading...</div>
                <Link to="/package" className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 mt-3">
                    뒤로가기
                </Link>
            </div>
        );
    }

    return (
        <div className="mx-4 [font-variant-numeric:tabular-nums]">
            <style>
                {`
                .seat-grid {
                    display: grid;
                    grid-template-columns: repeat(5, 1fr);
                    gap: 10px;
                }
                .seat-btn {
                    aspect-ratio: 1.2;
                    border-radius: 12px;
                    font-weight: 700;
                    font-size: 16px;
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: 1px solid #eee;
                    background: #fff;
                    color: #000;
                    transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, scale 0.15s ease;
                }
                .seat-btn:not(.disabled):active {
                    scale: 0.96;
                }
                .seat-btn.selected {
                    background: #e16036 !important;
                    color: #fff !important;
                    border-color: #e16036 !important;
                }
                .seat-btn.disabled {
                    background: #6b7280 !important;
                    color: #fff !important;
                    border-color: #6b7280 !important;
                    cursor: not-allowed;
                }
                .seat-btn.disabled .seat-label {
                    opacity: 0;
                }
                .seat-btn.disabled::after {
                    content: "/";
                    position: absolute;
                    inset: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: rgba(255, 255, 255, 0.95);
                    font-size: 18px;
                    font-weight: 800;
                    line-height: 1;
                    pointer-events: none;
                }
                `}
            </style>
            <div className="mt-xl-2 mt-1">
                {isOptionStep ? optionSelectionContent : (
                <>
                {/* Hero Section */}
                <div className="mb-4 overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
                    <div className="product-img-slider relative overflow-hidden">
                        {productImageItems.length ?
                            <AmCarousel interval={4000}
                                heightRatio={0.78}
                                objectFit="cover"
                                useImageRatio={false}
                                items={productImageItems} />
                            : <div className="h-72 w-full bg-gray-200"></div>
                        }
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex min-h-[42%] items-end bg-gradient-to-t from-black/72 via-black/34 to-transparent px-5 pb-5">
                            <div className="w-full min-w-0 pr-14">
                                <div className="min-w-0">
                                    <h3 className="mb-0 line-clamp-2 text-balance text-2xl font-bold leading-snug text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.58)]" style={{ fontFamily: "Gmarket-B" }}>
                                        {product.name}
                                    </h3>
                                    {product.simple_desc ? (
                                        <p className="mt-1.5 mb-0 line-clamp-1 text-sm font-medium leading-5 text-white/[0.82] drop-shadow-[0_1px_8px_rgba(0,0,0,0.48)]">
                                            {product.simple_desc}
                                        </p>
                                    ) : null}
                                </div>
                                <div className="mt-2 text-left text-xl font-bold text-orange-400 drop-shadow-[0_1px_8px_rgba(0,0,0,0.52)]" style={{ fontFamily: "Gmarket-B" }}>
                                    {numberWithCommas(product.price)}원
                                </div>
                                {taglineBadges.length > 0 ? (
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                        {taglineBadges.map((tagline, index) => (
                                            <span
                                                key={`${product.id || id}-detail-tagline-${index}-${tagline}`}
                                                className="inline-flex max-w-full items-center rounded-full border border-white/35 bg-white/15 px-2.5 py-1 text-[11px] font-semibold leading-4 text-white shadow-sm backdrop-blur-sm break-all"
                                            >
                                                {tagline}
                                            </span>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-center px-4 py-3 shadow-sm">
                        {privateOfferToken ? (
                            <div className="w-full max-w-[280px]">
                                <label className="mb-1 flex items-center justify-center gap-1.5 text-xs font-bold text-gray-500" htmlFor="private-offer-reservation-date">
                                    <CalendarIcon size={15} className="block shrink-0" />
                                    예약일
                                </label>
                                <CommonDatePicker
                                    id="private-offer-reservation-date"
                                    value={startDate}
                                    onChangeValue={handleStartDateChange}
                                    minDate={minimumSelectableDate}
                                    maxDate={privateOfferReservationEndDate || undefined}
                                    options={{
                                        altInput: true,
                                        altFormat: "Y.m.d",
                                    }}
                                    className="h-10 w-full rounded-xl border border-gray-200 bg-white px-3 text-center text-sm font-bold text-gray-900 shadow-sm outline-none focus:border-[#e16036] focus:ring-2 focus:ring-[#e16036]/15"
                                    placeholder="예약일 선택"
                                />
                            </div>
                        ) : (
                            <span className="inline-flex h-6 min-w-0 items-center justify-center gap-1.5 text-base font-bold leading-none text-gray-900" style={{ fontFamily: "Gmarket-M" }}>
                                <CalendarIcon size={18} className="block shrink-0 text-gray-900" />
                                <span className="block truncate leading-none">{formatCompactDateWithWeekday(startDate)}</span>
                            </span>
                        )}
                    </div>
                </div>

                <div className="-mx-4 h-2 bg-gray-200 mb-5" />


                {/* Seat Selection Header */}
                <div className="flex flex-col mb-4">
                    <div>
                        <div className="flex flex-col gap-3 mb-4 mt-5">
                            <div className="flex items-center justify-between gap-3 ">
                                <h3 className="text-xl font-bold" style={{ fontFamily: "Gmarket-B" }}>{seatSectionTitle}</h3>
                                <div className="flex items-center gap-2">
                                    {shouldShowSeatMapButton ? (
                                        <button
                                            type="button"
                                            className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-[#f0b39b] bg-white px-3 py-1.5 text-xs font-bold text-[#e16036] transition-[background-color,scale] duration-150 ease-out hover:bg-orange-50 active:scale-[0.96] focus:outline-none focus:ring-2 focus:ring-[#e16036]/20"
                                            onClick={() => setSeatImageModalOpen(true)}
                                        >
                                            <MapPin size={14} />
                                            배치도
                                        </button>
                                    ) : null}
                                    {seatLayoutNotice ? (
                                        <div
                                            className="relative"
                                            onMouseEnter={() => setSeatLayoutTooltipOpen(true)}
                                            onMouseLeave={() => setSeatLayoutTooltipOpen(false)}
                                        >
                                            <button
                                                type="button"
                                                className={`inline-flex items-center gap-1 px-0 py-0 text-xs font-medium ${seatLayoutNotice.triggerClassName}`}
                                                aria-label="좌석 안내 보기"
                                                aria-expanded={seatLayoutTooltipOpen}
                                                onClick={() => setSeatLayoutTooltipOpen((prev) => !prev)}
                                            >
                                                <Info size={14} />
                                                안내
                                            </button>

                                            {seatLayoutTooltipOpen ? (
                                                <div className={`absolute right-0 top-full z-10 mt-2 w-64 rounded-xl border px-4 py-3 text-sm leading-5 shadow-lg ${seatLayoutNotice.panelClassName}`}>
                                                    {seatLayoutNotice.message}
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                            {!isFreeSeatPackage && !seatLayoutLoading && !seatLayout?.seats?.length ? (
                                <div className="px-2">
                                    <div className="flex flex-wrap gap-3 text-xs">
                                        <div className="flex items-center gap-1">
                                            <div className="w-4 h-4 rounded-[5px] border border-gray-300 bg-white" />
                                            <span className="text-gray-900">가능</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <div className="w-4 h-4 rounded-[5px] border border-[#e16036] bg-[#e16036]" />
                                            <span className="text-gray-900">선택</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <div className="relative w-4 h-4 rounded-[5px] border  border-gray-600 bg-gray-600 overflow-hidden">
                                                <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white">/</span>
                                            </div>
                                            <span className="text-gray-900">불가</span>
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        {seatSelectionWarning ? (
                            <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium leading-6 text-amber-900 mx-2">
                                {seatSelectionWarning}
                            </div>
                        ) : null}

                        {isFreeSeatPackage ? (
                            <div className="flex flex-col gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-4">
                                <div className="flex items-center justify-between gap-4">
                                    <div>
                                        <p className="mb-1 text-sm font-bold text-[#2f2a24]" style={{ fontFamily: "Gmarket-M" }}>자유석 수량</p>
                                        <p className="mb-0 text-xs text-[#8b8377]">
                                            남은 수량 {freeSeatRemainingCount}개
                                        </p>
                                    </div>
                                    <QuantityButtonInput
                                        quantity={selectedSeats.length}
                                        detailPage={true}
                                        variant="subtle"
                                        onChange={handleFreeSeatQuantity}
                                    />
                                </div>
                                {freeSeatRemainingCount <= 0 ? (
                                    <p className="mt-3 mb-0 text-sm font-medium text-amber-700">예약 가능한 자유석이 없습니다.</p>
                                ) : null}
                            </div>
                        ) : product.has_spots ? (
                            <>
                                {seatLayoutLoading && !seatLayout ? (
                                    <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-sm text-gray-500 mx-2">
                                        좌석도를 불러오는 중입니다.
                                    </div>
                                ) : seatLayout && seatLayout.seats?.length ? (
                                    <div className="px-2">
                                        <SeatLayoutZoomViewer
                                            layout={seatLayout}
                                            selectedSeatIds={selectedSeats.map((item) => item.id)}
                                            onExpand={() => setSeatLayoutModalOpen(true)}
                                            onSelectSeat={(seat) => handleSeats({
                                                id: seat.spot_id,
                                                name: seat.label || seat.spot_name,
                                                is_able: seat.availability?.can_select,
                                            })}
                                        />
                                    </div>
                                ) : (
                                    <div className="px-4">
                                        <div className="seat-grid">
                                            {packageSeats && packageSeats.map((spot) => {
                                                const isSelected = selectedSeats && selectedSeats.find(item => item.id === spot.id);
                                                const isDisabled = !spot["is_able"] || (product.price === 0 && !product.allow_free_booking);

                                                return (
                                                    <button
                                                        key={spot.id}
                                                        className={`seat-btn ${isSelected ? "selected" : ""} ${isDisabled ? "disabled" : ""}`}
                                                        onClick={() => {
                                                            if (!isDisabled) handleSeats(spot);
                                                        }}
                                                        disabled={isDisabled}
                                                    >
                                                        <span className="seat-label">{spot.name}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : null}
                    </div>
                </div>
                <div className="-mx-4 h-2 bg-gray-200 mb-5" />

                <section className="mt-5 px-2 py-3">
                    <div className="mb-3  border border-gray-100 bg-orange-50 px-4 py-3.5">
                        <h4 className="mb-1 text-lg font-bold text-black" style={{ fontFamily: "Gmarket-B" }}>상품 상세</h4>
                        <p className="mb-0 text-xs font-medium text-gray-700">패키지 구성과 상세 내용을 확인해주세요.</p>
                    </div>
                    {product.descript ? (
                        <div
                            className="flex flex-wrap rounded-xl bg-white px-2 py-3.5 text-[14px] leading-6 text-gray-700 [&>*]:w-full [&_[style*='color']]:!text-red-500 [&_br]:hidden [&_img]:my-3 [&_img]:rounded-xl [&_li]:mb-1.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p:last-child]:mb-0 [&_p]:mb-2.5 [&_strong]:font-bold [&_strong]:text-red-500 [&_ul]:list-disc [&_ul]:pl-5"
                            dangerouslySetInnerHTML={{ __html: product.descript }}
                        />
                    ) : (
                        <p className="mb-0 rounded-xl bg-white px-4 py-4 text-sm text-gray-700">등록된 상품 상세 정보가 없습니다.</p>
                    )}
                </section>
                <div className="-mx-4 h-2 bg-gray-200 my-5" />

                {optionProducts.find((item) => item.type === "deposit") ? (
                    <section className="px-2 py-3">
                        <div className="mb-3  border border-gray-100 bg-orange-50 px-4 py-3.5">
                            <h4 className="mb-1 text-lg font-bold text-black" style={{ fontFamily: "Gmarket-B" }}>청소 보증금 안내</h4>
                            <p className="mb-0 text-xs font-medium text-gray-700">시설 정리 확인 후 환불되는 보증금 안내입니다.</p>
                        </div>
                        <div className="flex flex-wrap rounded-xl bg-white px-2 py-3.5 text-[14px] leading-6 text-gray-700 [&>*]:w-full [&_[style*='color']]:!text-red-500 [&_br]:hidden [&_p:last-child]:mb-0 [&_p]:mb-2.5 [&_strong]:font-bold [&_strong]:text-red-500">
                            <p>
                                <strong className="font-semibold text-red-500"> 청결한 시설 관리 및 비품 관리를 위해</strong> 패키지 상품 구매 시
                                <span className="block">
                                    청소보증금이 <strong className="font-semibold">자동결제</strong> 됩니다.
                                </span>
                            </p>
                            <p>
                                시설 이용 후 정리를 완료하고 체크아웃 버튼을 누르면 관리자가 정리 사항을 확인합니다.
                            </p>
                            <p className="mb-0">
                                <strong className="text-red-500">체크아웃 요청이 승인되면 자동 환불됩니다.</strong>
                            </p>
                        </div>
                    </section>
                ) : null}
                {optionProducts.find((item) => item.type === "deposit") ? <div className="-mx-4 h-2 bg-gray-200 my-5" /> : null}

                <section className="product-content mt-5 px-2 py-3">
                    <div className="mb-3  border border-gray-100 bg-orange-50 px-4 py-3.5">
                        <h5 className="mb-1 text-lg font-bold text-black" style={{ fontFamily: "Gmarket-B" }}>이용안내</h5>
                        <p className="mb-0 text-xs font-medium text-gray-700">예약 전 꼭 필요한 이용 정보를 확인해주세요.</p>
                    </div>
                    {product.using_info ? (
                        <div
                            className="flex flex-wrap rounded-xl bg-white px-2 py-3.5 text-[14px] leading-6 text-gray-700 [&>*]:w-full [&_[style*='color']]:!text-red-500 [&_br]:hidden [&_li]:mb-1.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p:last-child]:mb-0 [&_p]:mb-2.5 [&_strong]:font-bold [&_strong]:text-red-500 [&_ul]:list-disc [&_ul]:pl-5"
                            dangerouslySetInnerHTML={{ __html: product.using_info }}
                        />
                    ) : (
                        <p className="mb-0 rounded-xl bg-white px-4 py-4 text-sm text-gray-700">등록된 이용안내가 없습니다.</p>
                    )}
                </section>
                </>
                )}

                <div className={`h-20 mb-20 ${isOptionStep ? "-mx-4 bg-gray-100" : ""}`} />
            </div>


            <div
                className="fixed-footer"
                style={{
                    padding: "16px 20px 24px 20px",
                    maxWidth: "640px",
                    width: "100%",
                    backgroundColor: "#fff",
                    borderTop: "1px solid #ededed",
                    position: "fixed",
                    left: "50%",
                    transform: "translateX(-50%)",
                    bottom: "0",
                    zIndex: 1000,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    boxShadow: "0 -4px 10px rgba(0,0,0,0.03)"
                }}
            >

                {/* Removed Footer Popup - Moved to Bottom Sheet */}

                {/* Left: Total Price */}
                <div className="flex flex-col">
                    <div className="flex items-baseline gap-1">
                        <span className="text-xl font-bold text-black" style={{ fontFamily: "Gmarket-B" }}>
                            {numberWithCommas(totalAmount)} <span className="text-sm font-normal">원</span>
                        </span>
                    </div>
                </div>

                {/* Right: Action Button */}
                <Button
                    variant="primary"
                    disabled={bookingLoading || selectedSeats.length === 0}
                    className={bookingLoading || selectedSeats.length === 0 ? "opacity-80 text-white/95" : ""}
                    onClick={handleBookingSubmit}
                >
                    {bookingButtonLabel}
                </Button>
            </div>

            <Modal isOpen={seatLayoutModalOpen} toggle={() => setSeatLayoutModalOpen(false)} variant="full" className="gap-0 border-0 bg-white">
                <div className="flex min-h-full flex-col bg-white">
                    <ModalHeader toggle={() => setSeatLayoutModalOpen(false)} showBack>
                        좌석 배치도
                    </ModalHeader>

                    <div className="flex-1 overflow-y-auto px-4 py-4">
                        {seatLayout && seatLayout.seats?.length ? (
                            <SeatLayoutZoomViewer
                                layout={seatLayout}
                                selectedSeatIds={selectedSeats.map((item) => item.id)}
                                viewportClassName="h-[calc(100vh-190px)] min-h-[420px]"
                                onSelectSeat={(seat) => handleSeats({
                                    id: seat.spot_id,
                                    name: seat.label || seat.spot_name,
                                    is_able: seat.availability?.can_select,
                                })}
                            />
                        ) : (
                            <div className="rounded-3xl border border-dashed border-[#eadfce] bg-[#fffaf3] px-4 py-8 text-center text-sm leading-6 text-[#8b8377]">
                                {seatLayoutMessage || "배치도를 불러올 수 없습니다."}
                            </div>
                        )}
                    </div>
                </div>
            </Modal>

            <Modal isOpen={seatImageModalOpen} toggle={() => setSeatImageModalOpen(false)} variant="full" className="gap-0 border-0 bg-white">
                <div className="flex min-h-full flex-col bg-white">
                    <ModalHeader toggle={() => setSeatImageModalOpen(false)} showBack>
                        배치도 이미지
                    </ModalHeader>

                    <div className="flex flex-1 items-center justify-center overflow-auto bg-[#111827] px-4 py-4">
                        {product["seat_img"] ? (
                            <img
                                src={product["seat_img"]}
                                alt="기존 배치도 이미지"
                                className="max-h-full w-full object-contain"
                            />
                        ) : (
                            <div className="rounded-3xl border border-dashed border-white/20 bg-white/5 px-4 py-8 text-center text-sm leading-6 text-white/70">
                                기존 배치도 이미지를 불러올 수 없습니다.
                            </div>
                        )}
                    </div>
                </div>
            </Modal>

            <DetailImgModal setVisible={setDetailImageEditModal} visible={detailImageEditModal} itemId={product.id} type={"package"} navigate={navigate} />
            <DetailInfoModal setVisible={setDetailInfoModal} visible={detailInfoModal} itemId={product.id} type={"package"} navigate={navigate} />
            <DetailUseAdviceModal setVisible={setDetailUseAdviceModal} visible={detailUseAdviceModal} itemId={product.id} type={"package"} navigate={navigate} />
            <LoginPromptModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
        </div>
    );
}

export default PackageDetailSection;
